import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import axios from "axios";
import { useSelector } from "react-redux";
import { api_url } from "../config";
import { useNavigate } from "react-router-dom";
import { MdClose } from "react-icons/md";
import api from "../utils/api";

// We'll initialize the Stripe promise in the component
let stripePromise = null;

const UpdateCardDetailsForm = ({ toggle, stripeKey, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { token, id, email } = useSelector((state) => state.auth);
  const [clientSecret, setClientSecret] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSetupIntent = async () => {
      try {
        const { data } = await api.get(
          `${api_url}/v1/user/${id}/setup-intent`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        console.log('Setup intent response:', data);
        setClientSecret(data.client_secret);
      } catch (err) {
        console.error('Error fetching setup intent:', err);
        setError("Failed to initialize payment setup. Please try again.");
      }
    };
    fetchSetupIntent();
  }, [id, token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      console.log('Confirming card setup with client secret:', clientSecret);
      
      const result = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: { email },
          },
        }
      );

      console.log('Stripe confirmation result:', result);

      if (result.error) {
        console.error('Stripe error:', result.error);
        setError(result.error.message);
      } else if (result.setupIntent && result.setupIntent.status === "succeeded") {
        try {
          const paymentMethodId = result.setupIntent.payment_method;
          console.log('Payment method ID:', paymentMethodId);
          
          const response = await axios.post(
            `${api_url}/v1/user/${id}/payment-method`,
            { payment_method: paymentMethodId },
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );
          console.log('Backend response:', response.data);
          setSuccess(true);
          // Call parent's onSuccess callback to trigger data refresh
          if (onSuccess) {
            onSuccess();
          }
          setTimeout(() => {
            toggle();
          }, 2500);
        } catch (err) {
          console.error("Card update error:", err.response ? err.response.data : err);
          setError(err.response?.data?.error || "Failed to update payment method");
        }
      } else {
        console.error('Setup intent failed or returned unexpected status:', result.setupIntent);
        setError("Card setup failed. Please check your card details and try again.");
      }
    } catch (err) {
      console.error("Unexpected error during card setup:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full max-w-lg p-6 bg-white rounded-lg shadow-md"
    >
      <div className="flex justify-between">
        <h2 className="text-2xl font-semibold mb-4">Update Card Details</h2>
        <MdClose
          className="cursor-pointer hover:scale-105"
          onClick={() => {
            toggle();
          }}
          size={22}
        />
      </div>

      <div className="p-2 mb-4 border border-gray-300 rounded">
        <CardElement />
      </div>

      <button
        type="submit"
        disabled={!stripe || !elements || !clientSecret || loading}
        className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md shadow-sm w-full"
      >
        {loading ? "Updating..." : "Update Card"}
      </button>

      {error && (
        <div className="mt-4 text-red-800 bg-red-200 rounded-lg flex mx-auto">
          <p className="text-center mx-auto">{error}</p>
        </div>
      )}
      {success && (
        <div className="mt-4 bg-green-300 text-green-800 rounded-lg flex mx-auto">
          <p className="text-center mx-auto">
            Card details updated successfully!
          </p>
        </div>
      )}
    </form>
  );
};

const UpdateCardDetails = ({ toggle, onSuccess }) => {
  const [stripeKey, setStripeKey] = useState(process.env.REACT_APP_STRIPE_KEY);
  const [loading, setLoading] = useState(true);
  const { token, id } = useSelector((state) => state.auth);

  useEffect(() => {
    const fetchStripeKey = async () => {
      try {
        const { data } = await api.get(`${api_url}/v1/user/${id}/setup-intent`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        
        // If server provides a Stripe key, use it
        if (data.stripe_key) {
          setStripeKey(data.stripe_key);
          console.log('Using Stripe key from server:', data.stripe_key);
        }
      } catch (err) {
        console.error('Error fetching Stripe key:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStripeKey();
  }, [id, token]);

  // Initialize Stripe with the key
  if (!stripePromise && stripeKey) {
    console.log('Initializing Stripe with key:', stripeKey);
    stripePromise = loadStripe(stripeKey);
  }

  if (loading) {
    return <div>Loading payment setup...</div>;
  }

  return (
    <Elements stripe={stripePromise}>
      <UpdateCardDetailsForm toggle={toggle} stripeKey={stripeKey} onSuccess={onSuccess} />
    </Elements>
  );
};

export default UpdateCardDetails;
